import api from "services/api";
import { cpfMask } from "utils/cpf";
import { IContract } from "v2/pages/Workspace/WorkspaceListContracts/ListContracts/ListContracts.types";
import { contractLabel } from "v2/pages/Insights/contractLabel";

export const ALL_WORKSPACES_ID = "ALL";

export interface IInsightsWorkspaceOption {
  id: string;
  label: string;
  buyerName: string;
  buyerOrganizationId: string;
  buyerFederalTaxId: string;
}

export interface IInsightsAgentRequest {
  content: string;
  workspace_id: string;
  session_id: string;
}

export interface IInsightsContentBlock {
  type: "text" | "table";
  text?: string;
  rows?: Array<Record<string, any>>;
  label?: string;
}

export interface IInsightsAgentResponse {
  reply: string;
  blocks?: IInsightsContentBlock[];
  session_id: string;
  message_count: number;
}

export interface IInsightsPrefabParams {
  cpf?: string;
  dias_min?: number;
}

export interface IInsightsPrefabResponse {
  action: string;
  rows: Array<Record<string, any>>;
  message?: string;
}

/**
 * Se este bundle **não** é de produção.
 *
 * Deliberadamente `MODE`, e não `import.meta.env.DEV`: o `.env` do repositório
 * define `NODE_ENV=development`, e com isso o Vite deixa `DEV === true` também
 * no `vite build`. Conferido no bundle gerado (`DEV:!0`, `MODE:"production"`).
 * Ou seja, todo `if (import.meta.env.DEV)` deste arquivo estava **ligado em
 * produção** — o que faz um 503 real do backend virar número inventado na tela
 * do cliente em vez de erro. `MODE` é o único dos três que o build fixa
 * corretamente.
 */
const IS_DEV_BUILD = import.meta.env.MODE !== "production";

/**
 * Modo de demonstração da interface, ligado por `VITE_APP_INSIGHTS_MOCK=true`.
 *
 * Serve para revisar a tela sem depender de flag de organização, de allowlist de
 * buyer ou do backend com credencial do Snowflake. É explícito de propósito: um
 * fallback automático que inventasse dado quando a API falha esconderia
 * justamente o problema que a gente precisa ver.
 */
export const INSIGHTS_MOCK_ENABLED =
  IS_DEV_BUILD && import.meta.env.VITE_APP_INSIGHTS_MOCK === "true";

/** Dois contratos do mesmo CNPJ, para o seletor ter o que selecionar. */
export function getDevMockWorkspaces(
  organizationId: string
): IInsightsWorkspaceOption[] {
  return [
    {
      id: `${organizationId}-mock-saude`,
      label: "Saúde — QBem — #9784",
      buyerName: "Cliente de demonstração",
      buyerOrganizationId: organizationId,
      buyerFederalTaxId: "48785828009509",
    },
    {
      id: `${organizationId}-mock-odonto`,
      label: "Odontológico — Uniodonto — #1770",
      buyerName: "Cliente de demonstração",
      buyerOrganizationId: organizationId,
      buyerFederalTaxId: "48785828009509",
    },
  ];
}

export type IInsightsPrefabKind =
  | "pendencias"
  | "investigar-cpf"
  | "performance"
  | "anomalias"
  | "exportacao"
  | "resumo-executivo";

/**
 * Não há endpoint dedicado de listagem para o Insights — reaproveita a mesma
 * rota que `WorkspaceListContracts` usa para listar os contratos.
 *
 * A rota aceita qualquer organização e devolve os contratos dela: com o id do
 * fornecedor (usuário buyer navegando pelo sidebar) ou com o id do cliente
 * (usuário seller que abriu o painel dentro de um CNPJ). O segundo caso é o que
 * faz o painel funcionar para corretora — percorrer os fornecedores do sidebar
 * não devolve nada para quem está logado como seller.
 */
export async function getWorkspacesForOrganization(
  organizationId: string
): Promise<IInsightsWorkspaceOption[]> {
  const response = await api.get<{ data: IContract[] }>(
    `/v2/organizations/${organizationId}/workspaces`
  );
  const contracts = response.data.data ?? [];

  return contracts.map((contract) => ({
    id: contract.id,
    label: contractLabel(contract),
    buyerName: contract.buyerOrganization?.corporateName ?? "",
    buyerOrganizationId: contract.buyerOrganization?.id ?? "",
    buyerFederalTaxId: contract.buyerOrganization?.federalTaxId ?? "",
  }));
}

/**
 * Fallback dev-only: em ambiente local o backend precisa de
 * `snowflake_key.pem` (chave privada de autenticação JWT com o Snowflake)
 * pra chamar o Cortex Agent de verdade. Sem essa chave, /insights/agent
 * sempre responde 503 — o que é o comportamento correto, não um bug.
 * Enquanto a chave real não está disponível, isso deixa dar preview do
 * layout de conversa multi-turn localmente. `IS_DEV_BUILD` mantém esse caminho
 * fora do bundle de produção — ver a nota lá em cima sobre por que
 * `import.meta.env.DEV` não serve para isso neste repositório.
 * TODO: remover assim que snowflake_key.pem estiver configurado localmente.
 */
const DEV_MOCK_SESSION_COUNTS = new Map<string, number>();

const DEV_MOCK_REPLIES = [
  "Encontrei 3 pendências de aprovação neste workspace: 2 inclusões e 1 exclusão aguardando há mais de 5 dias.",
  "A última execução processou 128 beneficiários, com 4 inconsistências cadastrais — a maioria por CPF duplicado.",
  "Comparando com o mês anterior, o volume de movimentações caiu 12%, principalmente por menos admissões em julho.",
  "Não encontrei anomalias críticas nas últimas 24h. Só um pico de exclusões no dia 28, dentro do esperado pelo calendário de fechamento.",
  "Resumo executivo: 1.240 vidas ativas, 96% de cobertura documental e 3 contratos com renovação prevista pros próximos 60 dias.",
];

function getDevMockAgentResponse(
  request: IInsightsAgentRequest
): IInsightsAgentResponse {
  const messageCount = (DEV_MOCK_SESSION_COUNTS.get(request.session_id) ?? 0) + 1;
  DEV_MOCK_SESSION_COUNTS.set(request.session_id, messageCount);

  const mockText = `[mock local — sem snowflake_key.pem] ${DEV_MOCK_REPLIES[(messageCount - 1) % DEV_MOCK_REPLIES.length]}`;

  return {
    reply: mockText,
    blocks: [{ type: "text", text: mockText }],
    session_id: request.session_id,
    message_count: messageCount,
  };
}

export async function sendInsightsAgentMessage(
  request: IInsightsAgentRequest,
  signal?: AbortSignal
): Promise<IInsightsAgentResponse> {
  if (INSIGHTS_MOCK_ENABLED) return getDevMockAgentResponse(request);
  try {
    const response = await api.post<IInsightsAgentResponse>(
      "/insights/agent",
      request,
      { signal }
    );
    return response.data;
  } catch (error: any) {
    if (IS_DEV_BUILD && error?.response?.status === 503) {
      return getDevMockAgentResponse(request);
    }
    throw error;
  }
}

async function callInsightsPrefab(
  kind: IInsightsPrefabKind,
  workspaceId: string,
  params?: IInsightsPrefabParams
): Promise<IInsightsPrefabResponse> {
  if (INSIGHTS_MOCK_ENABLED) return getDevMockPrefabResponse(kind, params);
  const response = await api.post<IInsightsPrefabResponse>(
    `/insights/prefab/${kind}`,
    { workspace_id: workspaceId, params }
  );
  return response.data;
}

/**
 * Linhas com a forma que o `AssistantMessage` renderiza em tabela — o ponto da
 * demonstração é ver o bloco de tabela, não só texto.
 */
function getDevMockPrefabResponse(
  kind: IInsightsPrefabKind,
  params?: IInsightsPrefabParams
): IInsightsPrefabResponse {
  if (kind === "investigar-cpf") {
    // O prefab recebe só dígitos; a tabela mostra mascarado, como o relatório
    // real faz.
    const cpf = params?.cpf ? cpfMask(params.cpf) : "529.982.247-25";
    return {
      action: kind,
      rows: [
        {
          CPF: cpf,
          EVENTO: "Inclusão de titular",
          DATA: "2026-07-14",
          SITUACAO: "Aprovado",
          RETORNO_OPERADORA: "Carteirinha 9784-2201",
        },
        {
          CPF: cpf,
          EVENTO: "Inclusão de dependente",
          DATA: "2026-07-14",
          SITUACAO: "Aprovado",
          RETORNO_OPERADORA: "Carteirinha 9784-2202",
        },
      ],
    };
  }

  return {
    action: kind,
    rows: [
      {
        LOTE: "2026-08-05",
        TIPO: "Inclusão",
        PESSOAS: 12,
        LINHAS: 27,
        CRITICAS: 0,
        SITUACAO: "Concluído",
      },
      {
        LOTE: "2026-08-05",
        TIPO: "Exclusão",
        PESSOAS: 3,
        LINHAS: 5,
        CRITICAS: 1,
        SITUACAO: "Com crítica",
      },
      {
        LOTE: "2026-07-29",
        TIPO: "Inclusão",
        PESSOAS: 8,
        LINHAS: 19,
        CRITICAS: 0,
        SITUACAO: "Concluído",
      },
    ],
  };
}

export const getPendenciasDoDia = (workspaceId: string) =>
  callInsightsPrefab("pendencias", workspaceId);

export const investigarCpf = (workspaceId: string, cpf: string) =>
  callInsightsPrefab("investigar-cpf", workspaceId, { cpf });

export const getPerformance = (workspaceId: string) =>
  callInsightsPrefab("performance", workspaceId);

export const getAnomalias = (workspaceId: string) =>
  callInsightsPrefab("anomalias", workspaceId);

export const getUltimaExportacao = (workspaceId: string) =>
  callInsightsPrefab("exportacao", workspaceId);

export const getResumoExecutivo = (workspaceId: string) =>
  callInsightsPrefab("resumo-executivo", workspaceId);

/**
 * Os relatórios legados, de payload fixo, que existiam como botões escritos à
 * mão na barra do chat. Os novos **não** entram aqui: vêm do catálogo
 * (`getInsightsReportCatalog`) com id, título e parâmetros, então acrescentar
 * relatório no backend não exige mexer no front.
 */
export type InsightsLegacyReportType = "pendencias" | "performance" | "executivo";

/**
 * O id de um relatório em `POST /insights/report/:type`. `string` de propósito:
 * o conjunto dos válidos é o do backend, e o front descobre pelo catálogo.
 */
export type InsightsReportId = string;

export interface IInsightsReportRequest {
  workspace_id: string;
  /** Relatórios com `lista-cpf`. */
  cpfs?: string[];
  /** Relatórios com `intervalo-data`, `YYYY-MM-DD`. */
  desde?: string;
  /** Idem, inclusivo. */
  ate?: string;
}

export interface IInsightsReportResponse {
  html: string;
  filename: string;
  title: string;
  /**
   * Só nos relatórios do registry novo. `false` = não se aplica a este
   * workspace e o `html` traz o motivo em vez de dado.
   */
  disponivel?: boolean;
  motivo?: string;
  /**
   * Presente nos relatórios cujo entregável é um arquivo (o R4). Quando vem, é
   * ele que o usuário baixa — o `html` fica de fora.
   */
  csv?: string;
  csvFilename?: string;
  /**
   * O relatório recusou gerar o arquivo e disse por quê: período sem exportação,
   * ou resultado acima do teto. **Não é erro** — vem com `disponivel: true`, e o
   * certo é mostrar o texto em vez de baixar nada.
   */
  csvMotivo?: string;
}

/** O que o relatório pede ao usuário. Espelha o `ParamSpec` do backend. */
export type IInsightsReportParamSpec =
  | { kind: "intervalo-data"; label: string }
  | { kind: "lista-cpf"; label: string; max: number };

export interface IInsightsReportCatalogItem {
  id: InsightsReportId;
  titulo: string;
  descricao: string;
  cadence: string;
  /** Token de cor, não valor — quem resolve para hex é o tema do front. */
  accent: "blue" | "orange";
  params: IInsightsReportParamSpec[];
  /**
   * `false` = o relatório não se aplica a este workspace. O card continua na
   * tela, desabilitado, com o `motivo` visível — sumir deixaria o usuário sem
   * saber que o relatório existe nem por que não está lá.
   */
  disponivel: boolean;
  motivo?: string;
}

/**
 * Mesmo racional do DEV_MOCK_REPLIES acima: localmente, /insights/report/:type
 * ainda não existe no backend (só /enabled, /agent e /prefab/:action estão
 * registrados em routes/insights/index.ts), então a chamada real vira 404 hoje
 * e viraria 503 se a rota existir mas depender de credencial Snowflake não
 * configurada localmente — tratamos os dois como "sem backend disponível" só
 * em dev.
 * TODO: remover o fallback de 404 assim que o backend publicar a rota.
 */
const DEV_MOCK_REPORT_TITLES: Record<string, string> = {
  pendencias: "Relatório de Pendências",
  performance: "Relatório de Performance",
  executivo: "Resumo Executivo",
  "timeline-cpf": "Investigação por CPF",
  "movimentacoes-periodo": "Ciclo de Movimentação",
  "cobertura-confirmada": "Cobertura Confirmada",
};

function getDevMockReportResponse(
  type: InsightsReportId,
  request: IInsightsReportRequest
): IInsightsReportResponse {
  const title = DEV_MOCK_REPORT_TITLES[type] ?? type;

  // O R4 entrega **arquivo**, não página: a demonstração precisa exercitar esse
  // caminho, senão valida a experiência errada. Duas linhas com o dialeto real
  // (BOM, `;`, CPF mascarado) para dar para abrir no Excel e conferir.
  if (type === "exportados-periodo") {
    const de = request.desde ?? "2026-08-01";
    const ate = request.ate ?? "2026-08-17";
    const cabecalho =
      "data_exportacao;lote;nome;cpf;tipo_pessoa;data_nascimento;tipo_movimento;plano;data_exclusao_plano;cpf_titular;matricula;cargo;data_admissao;data_demissao;cidade;pessoa_juridica;ciclos_no_periodo;aprovado_por_no_lote";
    const linhas = [
      "05/08/2026 17:13;3978eeee-9013-42d6-bff7-d45129df0957;[mock] MARIA DA SILVA;529.982.247-25;Titular;14/03/1985;Inclusão;106 - KLINI TOTAL EMP COM COPART;;529.982.247-25;12601873;SOLDADOR MIGER;01/07/2025;;1302504 - Manacapuru;Não;1;Talita Oliveira",
      "05/08/2026 17:13;3978eeee-9013-42d6-bff7-d45129df0957;[mock] JOAO DE SOUZA;111.444.777-35;Dependente;02/02/1995;Exclusão;106 - KLINI TOTAL EMP COM COPART;01/09/2026;529.982.247-25;12601874;;;;2805604 - Porto da Folha;Não;2;Talita Oliveira",
    ];

    return {
      title,
      filename: `mock-${type}.html`,
      html: "",
      csv: `\ufeff${[cabecalho, ...linhas].join("\r\n")}\r\n`,
      csvFilename: `exportados_mock_${de}_a_${ate}.csv`,
    };
  }
  const scope = request.cpfs?.length
    ? `cpfs: ${request.cpfs.join(", ")}`
    : `workspace_id: ${request.workspace_id}`;

  return {
    title,
    filename: `relatorio-${type}-mock.html`,
    html: `<html><head><meta charset="utf-8"><title>${title}</title></head><body><h1>${title}</h1><p>[mock local — sem backend /insights/report/${type}] ${scope}</p></body></html>`,
  };
}

export async function generateInsightsReport(
  type: InsightsReportId,
  request: IInsightsReportRequest
): Promise<IInsightsReportResponse> {
  if (INSIGHTS_MOCK_ENABLED) return getDevMockReportResponse(type, request);
  try {
    const response = await api.post<IInsightsReportResponse>(
      `/insights/report/${type}`,
      request
    );
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    if (IS_DEV_BUILD && (status === 503 || status === 404)) {
      return getDevMockReportResponse(type, request);
    }
    throw error;
  }
}

/**
 * Mesmo racional dos mocks acima, e só em dev: sem `snowflake_key.pem` o
 * catálogo responde 503 porque não consegue sondar o schema do workspace. O
 * mock devolve os cards **disponíveis** de propósito — quem quer ver o caminho
 * de indisponibilidade tem o backend real para isso.
 * TODO: remover quando a credencial local existir.
 */
const DEV_MOCK_CATALOG: IInsightsReportCatalogItem[] = [
  {
    id: "movimentacoes-periodo",
    titulo: "Ciclo de Movimentação",
    descricao:
      "O que foi aprovado no período, por lote, com pessoas e linhas — e o que a operadora criticou.",
    cadence: "Relatório sob demanda",
    accent: "blue",
    params: [{ kind: "intervalo-data", label: "Período de aprovação" }],
    disponivel: true,
  },
  {
    id: "exportados-periodo",
    titulo: "Exportados no período",
    descricao:
      "Quem entrou nas remessas enviadas à operadora entre duas datas, com o lote de cada uma.",
    cadence: "Relatório sob demanda",
    accent: "blue",
    params: [{ kind: "intervalo-data", label: "Período de exportação" }],
    disponivel: true,
  },
  {
    id: "timeline-cpf",
    titulo: "Investigação por CPF",
    descricao: "A linha do tempo de cada CPF, do evento de origem ao retorno da operadora.",
    cadence: "Relatório sob demanda",
    accent: "blue",
    params: [{ kind: "lista-cpf", label: "CPFs a investigar", max: 10 }],
    disponivel: true,
  },
  {
    id: "cobertura-confirmada",
    titulo: "Cobertura Confirmada",
    descricao:
      "Quantos dos aprovados aparecem com carteirinha ativa na última coleta da operadora — e quem não apareceu.",
    cadence: "Relatório sob demanda",
    accent: "blue",
    params: [],
    disponivel: true,
  },
];

/**
 * O catálogo de relatórios do workspace, com disponibilidade por workspace.
 *
 * Card indisponível **não** é filtrado aqui: a tela mostra o motivo. É o
 * backend que decide o que existe — o front não mantém lista paralela.
 */
export async function getInsightsReportCatalog(
  workspaceId: string
): Promise<IInsightsReportCatalogItem[]> {
  if (INSIGHTS_MOCK_ENABLED) {
    // Um card indisponível junto dos três: a tela de relatórios tem dois
    // estados, e uma demonstração que só mostra o feliz esconde metade do que
    // foi desenhado — o card fica na tela, com o motivo escrito (D7).
    return [
      ...DEV_MOCK_CATALOG,
      {
        id: "cobertura-confirmada-demo",
        titulo: "Exemplo de relatório indisponível",
        descricao:
          "Como a tela se comporta quando o contrato não tem o dado que o relatório exige.",
        cadence: "Relatório sob demanda",
        accent: "orange",
        params: [],
        disponivel: false,
        motivo:
          "Este contrato ainda não tem coleta de carteirinha da operadora. Assim que a primeira coleta rodar, o relatório fica disponível.",
      },
    ];
  }
  try {
    const response = await api.get<{ reports: IInsightsReportCatalogItem[] }>(
      "/insights/reports",
      { params: { workspace_id: workspaceId } }
    );
    return response.data.reports ?? [];
  } catch (error: any) {
    const status = error?.response?.status;
    if (IS_DEV_BUILD && (status === 503 || status === 404)) {
      return DEV_MOCK_CATALOG;
    }
    throw error;
  }
}

export { getInsightsErrorMessage } from "./insightsErrorMessage";

export interface IInsightsEnabledResponse {
  enabled: boolean;
  qualifyingBuyerIds: string[];
  /**
   * O assistente (chat + prefabs) está publicado? Não é derivado de `enabled`:
   * a aba pode abrir para os relatórios com o chat ainda indisponível na conta,
   * que é o estado de hoje fora do piloto. As rotas do chat estão fechadas pela
   * mesma flag, então esconder a aba não é a única defesa.
   */
  assistantEnabled: boolean;
}

/**
 * Público (sem auth) — o gate é por conta Snowflake, não por sessão de
 * usuário, então dá pra decidir se mostra o item de sidebar/FAB/rota antes
 * de qualquer chamada autenticada.
 *
 * `qualifying_buyer_ids` lista os buyers cujos dados existem no Snowflake —
 * usado para filtrar a listagem de workspaces do seller (que por padrão
 * traz todos os buyers da corretora, mesmo os sem dado).
 */
export async function getInsightsEnabled(
  organizationId: string
): Promise<IInsightsEnabledResponse> {
  // No modo de demonstração o gate abre sozinho: sem isso o FAB nem aparece, e
  // não haveria como chegar na tela que se quer ver. `qualifyingBuyerIds` fica
  // vazio de propósito — vazio é "não filtra nada".
  if (INSIGHTS_MOCK_ENABLED) {
    return { enabled: true, qualifyingBuyerIds: [], assistantEnabled: true };
  }
  const response = await api.get<{
    enabled: boolean;
    qualifying_buyer_ids?: string[];
    assistant_enabled?: boolean;
  }>("/insights/enabled", {
    params: { organization_id: organizationId },
  });
  return {
    enabled: Boolean(response.data.enabled),
    qualifyingBuyerIds: response.data.qualifying_buyer_ids ?? [],
    // Ausente conta como escondido: um backend que ainda não conhece o campo
    // também não tem o gate das rotas do chat, e mostrar a aba levaria a 403.
    assistantEnabled: response.data.assistant_enabled === true,
  };
}
